import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, getWorkspaceId, setWorkspaceId as persistWorkspaceId } from "../lib/api";

const AuthContext = createContext(null);

// If the URL has `?ws=<owner_id>` on first paint (either the app URL that a
// user was sent, or a link produced by "Open in new tab"), seed the per-tab
// override before AuthContext runs its own workspace resolution. We only touch
// sessionStorage — never localStorage — so the "default" workspace across all
// tabs is unaffected.
(function seedTabWorkspaceFromURL() {
  try {
    const params = new URLSearchParams(window.location.search);
    const wsParam = params.get("ws");
    if (wsParam) {
      sessionStorage.setItem("workspace_id", wsParam);
      // Strip the query param so refreshes don't keep forcing it if the user
      // manually switches workspaces later in the same tab.
      params.delete("ws");
      const q = params.toString();
      const newUrl = window.location.pathname + (q ? `?${q}` : "") + window.location.hash;
      window.history.replaceState({}, "", newUrl);
    }
  } catch { /* ignore */ }
})();

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null=checking, false=guest, object=authed
  const [workspaces, setWorkspaces] = useState(null);
  const [activeWorkspace, setActiveWorkspace] = useState(null);
  const [mode, setMode] = useState(() => localStorage.getItem("app_mode") || "live");

  const loadMe = useCallback(async () => {
    const token = localStorage.getItem("access_token");
    if (!token) {
      setUser(false);
      return;
    }
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      localStorage.removeItem("access_token");
      setUser(false);
    }
  }, []);

  const loadWorkspaces = useCallback(async () => {
    try {
      const { data } = await api.get("/workspaces");
      setWorkspaces(data);
      // Prefer per-tab override (sessionStorage / ?ws=), fall back to the
      // browser-wide default (localStorage).
      const stored = getWorkspaceId();
      const all = [data.own, ...data.shared];
      const found = all.find((w) => w.owner_id === stored);
      const chosen = found || data.own;
      setActiveWorkspace(chosen);
      // Persist to sessionStorage so this tab stays on the chosen workspace
      // even after the interceptor is next called. Only touch localStorage
      // (browser-wide default) when there is no per-tab override yet.
      if (chosen.owner_id === data.own.owner_id) {
        sessionStorage.removeItem("workspace_id");
        // Preserve the user's global default — don't nuke it here.
      } else {
        sessionStorage.setItem("workspace_id", chosen.owner_id);
      }
    } catch (e) {
      // ignore
    }
  }, []);

  useEffect(() => {
    loadMe();
  }, [loadMe]);

  useEffect(() => {
    if (user && user.id) {
      loadWorkspaces();
    }
  }, [user, loadWorkspaces]);

  const switchWorkspace = (ws) => {
    setActiveWorkspace(ws);
    if (ws.level === "owner") {
      // Clear both — own workspace is the natural default in every tab.
      persistWorkspaceId(null);
    } else {
      // Persist to BOTH: this tab uses the shared workspace immediately, and
      // it becomes the new global default so future new tabs open into the
      // same workspace unless they explicitly override via ?ws=.
      persistWorkspaceId(ws.owner_id);
    }
    window.dispatchEvent(new Event("workspace-changed"));
  };

  const switchMode = (newMode) => {
    localStorage.setItem("app_mode", newMode);
    setMode(newMode);
    // Demo mode forces own workspace
    if (newMode === "demo" && workspaces) {
      setActiveWorkspace(workspaces.own);
      persistWorkspaceId(null);
    }
    window.dispatchEvent(new Event("workspace-changed"));
  };

  const login = (token, u) => {
    localStorage.setItem("access_token", token);
    setUser(u);
  };

  const logout = () => {
    localStorage.removeItem("access_token");
    persistWorkspaceId(null);
    setUser(false);
    setWorkspaces(null);
    setActiveWorkspace(null);
  };

  return (
    <AuthContext.Provider
      value={{ user, setUser, login, logout, workspaces, activeWorkspace, switchWorkspace, refreshWorkspaces: loadWorkspaces, mode, switchMode }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
